// src/routes/MainRouter.tsx
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useTranslation } from "react-i18next";
import { Suspense, lazy } from "react";

// Fallback component
const Loading = () => <div>Loading...</div>;

// Lazy-loaded components
const LoginPage = lazy(() =>
  import("@/pages/LoginPage").then((module) => ({ default: module.LoginPage }))
);
const SignupPage = lazy(() =>
  import("@/pages/SignupPage").then((module) => ({
    default: module.SignupPage,
  }))
);
const ProfilePage = lazy(() =>
  import("@/pages/ProfilePage").then((module) => ({
    default: module.ProfilePage,
  }))
);
const ChunkPlayerScreen = lazy(() => import("@/pages/ChunkPlayerScreen"));
const ChunkIntroScreen = lazy(() => import("@/pages/ChunkIntroScreen"));
const FullDialogueScreen = lazy(() => import("@/pages/FullDialogueScreen"));
const ScenarioSelectionScreen = lazy(() =>
  import("@/pages/ScenarioSelectionScreen").then((module) => ({
    default: module.ScenarioSelectionScreen,
  }))
);
const LanguageIslandsEditor = lazy(() => import("@/pages/Islands"));
const IslandsListView = lazy(() => import("@/pages/IslandsListView"));
const IslandDetailView = lazy(() => import("@/pages/IslandDetailView"));
const ScenarioLayout = lazy(() => import("@/layouts/ScenarioLayout"));
// Static articles now served at /articles/index.html - no React component needed
const ReviewPage = lazy(() => import("@/pages/ReviewPage"));
const AchievementsPage = lazy(() => import("@/pages/AchievementsPage").then(m => ({ default: m.AchievementsPage })));

export function MainRouter() {
  const { t } = useTranslation();
  const { currentUser, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <p className="text-2xl text-white animate-pulse font-semibold tracking-wide">
          {t("gettingThingsSpicy")}
        </p>
      </div>
    );
  }

  const isAuthenticated =
    currentUser && !currentUser.isAnonymous && !!currentUser.email;

  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        {/* Public / Auth Routes */}
        <Route
          path="/login"
          element={
            isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />
          }
        />
        <Route
          path="/signup"
          element={
            isAuthenticated ? <Navigate to="/" replace /> : <SignupPage />
          }
        />

        {/* Routes accessible to all users */}
        <Route path="/" element={<ScenarioSelectionScreen />} />

        {/* Islands Routes */}
        <Route path="/islands" element={<IslandsListView />} />
        <Route path="/islands/new" element={<LanguageIslandsEditor />} />
        <Route path="/islands/view/:id" element={<IslandDetailView />} />
        <Route path="/islands/:id/edit" element={<LanguageIslandsEditor />} />

        <Route element={<ScenarioLayout />}>
          <Route
            path="/scenario-intro/:scenarioId"
            element={<ChunkIntroScreen />}
          />
          <Route path="/scenario/:scenarioId" element={<ChunkPlayerScreen />} />
          <Route
            path="/scenario-full/:scenarioId"
            element={<FullDialogueScreen />}
          />
        </Route>

        {/* Authenticated Routes */}
        <Route
          path="/profile"
          element={
            isAuthenticated ? <ProfilePage /> : <Navigate to="/login" replace />
          }
        />
        <Route
          path="/review"
          element={
            isAuthenticated ? <ReviewPage /> : <Navigate to="/login" replace />
          }
        />
        <Route
          path="/profile/achievements"
          element={
            isAuthenticated ? <AchievementsPage /> : <Navigate to="/login" replace />
          }
        />
        {/* Articles now served as static HTML at /articles/index.html */}
      </Routes>
    </Suspense>
  );
}